import {
  AlignmentType,
  BorderStyle,
  ShadingType,
  Table,
  TableRow,
  WidthType,
  convertMillimetersToTwip,
} from 'docx'
import { goPricingLayoutEngine } from '@/components/expGoPricing/goPricingSheetLayout'
import {
  createBoldParagraph,
  createParagraph,
  createTableCell,
  createTableNoteParagraph,
  DEFAULT_TABLE_MARGINS,
  TIGHT_TABLE_SPACING,
  createEmptyLine,
} from '../docxFunctions'
import { BOLD_TEXT, createCombinationParagraph } from '@/components/report/docxFunctions'

const HEADER_SHADING = {
  type: ShadingType.SOLID,
  color: 'd9d9d9',
}

const INVISIBLE_BORDER = {
  style: BorderStyle.NONE,
  size: 0,
  color: 'FFFFFF',
}

const PROCUREMENT_LEADING_BLANK_BORDERS = {
  top: INVISIBLE_BORDER,
  bottom: INVISIBLE_BORDER,
  left: INVISIBLE_BORDER,
  right: INVISIBLE_BORDER,
}

const ZERO_CELL_MARGINS = {
  top: convertMillimetersToTwip(0),
  bottom: convertMillimetersToTwip(0),
  left: convertMillimetersToTwip(0),
  right: convertMillimetersToTwip(0),
}

/** Лёгкая подгонка «Закупок» к левому краю текста (призрачная колонка). */
const PROCUREMENT_TABLE_LEFT_NUDGE = -convertMillimetersToTwip(0.1)

function formatCell(value) {
  return goPricingLayoutEngine.formatCellValue(value)
}

function isBlankLeadingValue(value) {
  const t = formatCell(value).trim()
  if (!t) return true
  if (t === '-' || t === '~') return true
  if (/^закупки$/i.test(t)) return true
  return false
}

function leadingColumnHasContent(tableRows) {
  return (tableRows || []).some(row => {
    const cell = row?.[0]
    if (!cell || cell.hidden) return false
    return !isBlankLeadingValue(cell.value)
  })
}

function isProcurementLeadingBlankCell(cell, colIdx, layout) {
  if (layout?.layoutVariant !== 'procurement') return false
  if (colIdx !== 0 || !cell || cell.hidden) return false
  if ((Number(cell.rowspan) || 1) > 1 || (Number(cell.colspan) || 1) > 1) {
    return false
  }
  return isBlankLeadingValue(cell.value)
}

function resolveHeaderRowCount(layout) {
  if (Number.isFinite(layout?.headerRowCount) && layout.headerRowCount > 0) {
    return layout.headerRowCount
  }
  return layout?.emphasizeFirstRow ? 1 : 0
}

function createDocxTableRow(row, rowIndex, layout) {
  const headerRowCount = resolveHeaderRowCount(layout)
  const isHeaderRow = headerRowCount > 0 && rowIndex < headerRowCount
  const isShadedHeaderRow = !!layout?.emphasizeFirstRow && rowIndex === 0
  const cells = []

  const hideProcurementLeadingBlank =
    layout?.layoutVariant === 'procurement' &&
    !leadingColumnHasContent(layout?.tableRows || [])

  for (let colIdx = 0; colIdx < (row || []).length; colIdx++) {
    const cell = row[colIdx]
    if (!cell || cell.hidden) continue
    const merged =
      (Number(cell.rowspan) || 1) > 1 || (Number(cell.colspan) || 1) > 1
    const text = formatCell(cell.value)
    const align =
      isHeaderRow || merged ? AlignmentType.CENTER : AlignmentType.LEFT
    const isLeadingBlank =
      hideProcurementLeadingBlank &&
      isProcurementLeadingBlankCell(cell, colIdx, layout)

    cells.push(
      createTableCell({
        rowSpan: cell.rowspan > 1 ? cell.rowspan : undefined,
        columnSpan: cell.colspan > 1 ? cell.colspan : undefined,
        width: isLeadingBlank ? { size: 0, type: WidthType.DXA } : undefined,
        borders: isLeadingBlank ? PROCUREMENT_LEADING_BLANK_BORDERS : undefined,
        margins: isLeadingBlank ? ZERO_CELL_MARGINS : undefined,
        shading: isShadedHeaderRow ? HEADER_SHADING : undefined,
        verticalAlign: merged ? 'center' : 'top',
        children: isHeaderRow
          ? [createBoldParagraph(text, { alignment: align }, 22)]
          : [createParagraph(text, { alignment: align }, {}, 22)],
      })
    )
  }

  return cells.length ? new TableRow({ children: cells }) : null
}

export function createGoPricingLayoutTable(layout) {
  const tableRows = layout?.tableRows || []
  if (!tableRows.length) return null

  const docxRows = tableRows
    .filter(row => !goPricingLayoutEngine.isProcurementLeadingLabelRow(row))
    .map((row, idx) => createDocxTableRow(row, idx, layout))
    .filter(Boolean)

  if (!docxRows.length) return null

  const needsProcurementAlignFix =
    layout?.layoutVariant === 'procurement' &&
    !leadingColumnHasContent(tableRows)

  return new Table({
    margins: DEFAULT_TABLE_MARGINS,
    width: { size: 100, type: WidthType.PERCENTAGE },
    spacing: TIGHT_TABLE_SPACING,
    alignment: AlignmentType.LEFT,
    ...(needsProcurementAlignFix
      ? {
          indent: {
            size: PROCUREMENT_TABLE_LEFT_NUDGE,
            type: WidthType.DXA,
          },
        }
      : {}),
    rows: docxRows,
  })
}

/** Абзацы и таблица по layout из GoPricingSheetGrid. */
export function createGoPricingLayoutBlocks(
  layout,
  { sectionTitle, trailingEmptyLine = true, showSectionTitle = true } = {}
) {
  if (!layout?.tableRows?.length && !layout?.title) return []

  const blocks = []
  const title = showSectionTitle ? sectionTitle || layout.title : ''

  if (title) {
    blocks.push(
      createCombinationParagraph(
        [
          {
            type: BOLD_TEXT,
            content: title,
          },
        ],
        { spacing: { after: 0, before: 0 } }
      )
    )
  }

  if (layout.formTitle) {
    blocks.push(
      createParagraph(
        String(layout.formTitle),
        { alignment: AlignmentType.LEFT, spacing: { after: 0, before: 0 } }
      )
    )
  }

  const table = createGoPricingLayoutTable(layout)
  if (table) blocks.push(table)

  if (layout.note) {
    blocks.push(
      ...String(layout.note)
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => createTableNoteParagraph(line))
    )
  }

  if (trailingEmptyLine) blocks.push(createEmptyLine())
  return blocks
}
